<template>
  <div v-if="customer" class="flex items-start">
    <div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center mb-2">
          <o-avatar :items="[customer]" size="lg" class="mr-4 hidden md:block" />
          <div>
            <h2 class="text-lg font-bold leading-none/85 sm:truncate">
              {{ customer.displayName }}
            </h2>
            <div class="flex flex-wrap md:flex-nowrap space-x-1 space-y-1 md:space-y-0 space-x-reverse items-center mt-2">
              <div v-if="phone || email" class="flex space-x-1 items-center">
                <o-panel-link
                  v-if="phone"
                  tag="o-button"
                  path="/message/send/sms"
                  :props="{ customers: [ customer ] }"
                  variant="primary"
                  size="sm"
                >
                  {{ $t('general.message') }}
                </o-panel-link>

                <o-button v-if="phone" outline size="sm" :href="'tel:'+phone">
                  {{ $t('general.call') }}
                </o-button>

                <o-panel-link
                  v-if="email"
                  tag="o-button"
                  path="/message/send/email"
                  :props="{ customers: [ customer ] }"
                  outline
                  size="sm"
                >
                  {{ $t('general.email') }}
                </o-panel-link>

                <div class="hidden md:block">
                  <div class="h-6 w-px border-r dark:border-gray-800 mx-2 block" />
                </div>
              </div>

              <div class="flex space-x-1 items-center">
                <o-panel-link
                  tag="o-button"
                  outline
                  size="sm"
                  :path="`/customer/edit?id=${customer.idCustomer}`"
                  :events="{ 'customer-updated' : updated }"
                >
                  {{ $t('general.edit') }}
                </o-panel-link>

                <o-panel-link
                  outline
                  size="sm"
                  icon-after
                  tag="o-button"
                  :path="`/customer/panel?id=${customer.idCustomer}`"
                >
                  {{ $t('components.customer.elements.preview.openProfile') }}
                </o-panel-link>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap">
          <o-description-details :term="$t('general.company')" base-class="py-1 pr-2 w-1/2">
            <template v-if="company">
              {{ company }}
            </template>
            <template v-else>
              <o-panel-link :path="`/customer/edit?id=${customer.idCustomer}`" class="opacity-75">
                {{ $t('components.customer.elements.preview.addCompany') }}
              </o-panel-link>
            </template>
          </o-description-details>
          <o-description-details :term="$t('general.mobile')" base-class="py-1 pr-2 w-1/2">
            <template v-if="phone">
              <a :href="`tel:${phone}`" target="_blank">{{ phone }}</a>
            </template>
            <template v-else>
              <o-panel-link :path="`/customer/edit?id=${customer.idCustomer}`" class="opacity-75">
                {{ $t('components.customer.elements.preview.addPhoneNumber') }}
              </o-panel-link>
            </template>
          </o-description-details>
          <o-description-details :term="$t('general.phone')" base-class="py-1 pr-2 w-1/2">
            <template v-if="homePhone">
              <a :href="`tel:${homePhone}`" target="_blank">{{ homePhone }}</a>
            </template>
            <template v-else>
              <o-panel-link :path="`/customer/edit?id=${customer.idCustomer}`" class="opacity-75">
                {{ $t('components.customer.elements.preview.addHomeNumber') }}
              </o-panel-link>
            </template>
          </o-description-details>
          <o-description-details :term="$t('general.email')" base-class="py-1 pr-2 w-1/2">
            <template v-if="email">
              <o-panel-link v-if="email" path="/message/send/email" :props="{ customers: [ customer ] }">
                {{ email }}
              </o-panel-link>
            </template>
            <template v-else>
              <o-panel-link :path="`/customer/edit?id=${customer.idCustomer}`" class="opacity-75">
                {{ $t('components.customer.elements.preview.addEmailAddress') }}
              </o-panel-link>
            </template>
          </o-description-details>
          <o-description-details v-if="customer.address" :term="$t('general.address')" base-class="py-1 w-full md:w-1/2">
            <template v-if="address">
              <a :href="`https://www.google.com/maps/search/?api=1&query=${address}`" target="_blank">{{ address }}</a>
            </template>
            <template v-else>
              <o-panel-link :path="`/customer/edit?id=${customer.idCustomer}`" class="opacity-75">
                {{ $t('components.customer.elements.preview.addAddress') }}
              </o-panel-link>
            </template>
          </o-description-details>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ODescriptionDetails,
  ODivider,
  OAvatar,
} from '@/components/elements'

import {
  TextField,
} from '@/components/fields'

interface Props {
  customer: Record<string, any>
}

const props = defineProps<Props>()

const demo = ref(0)

const company = computed(() => {
  return props.customer.company && props.customer.company.length ? props.customer.company : false
})

const phone = computed(() => {
  return props.customer.phone && props.customer.phone.length ? props.customer.phone : false
})

const homePhone = computed(() => {
  return props.customer.homePhone && props.customer.homePhone && props.customer.homePhone.length ? props.customer.homePhone : false
})

const email = computed(() => {
  return props.customer.email && props.customer.email.length ? props.customer.email : false
})

const address = computed(() => {
  return props.customer.addressLabel
})

const updated = (val: any) => {
  // Should we refresh the whole reservation to display the new customer details?
  demo.value = val
}
</script>
